import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    Box,
    Button,
    Grid,
    IconButton,
    styled,
    Typography,
} from '@mui/material';
import bgQuestionCheckbox from 'assets/images/backgrounds/question-type/bg-checkbox.jpg';
import bgQuestionDate from 'assets/images/backgrounds/question-type/bg-date.jpg';
import bgQuestionDropdown from 'assets/images/backgrounds/question-type/bg-dropdown.jpg';
import bgQuestionEmoji from 'assets/images/backgrounds/question-type/bg-emoji.jpg';
import bgQuestionMatrixCheckbox from 'assets/images/backgrounds/question-type/bg-matrix-checkbox.jpg';
import bgQuestionMatrixRadio from 'assets/images/backgrounds/question-type/bg-matrix-radio.jpg';
import bgQuestionMatrixTextbox from 'assets/images/backgrounds/question-type/bg-matrix-textbox.jpg';
import bgQuestionNumber from 'assets/images/backgrounds/question-type/bg-number.jpg';
import bgQuestionTextbox from 'assets/images/backgrounds/question-type/bg-question-textbox.jpg';
import bgQuestionRank from 'assets/images/backgrounds/question-type/bg-rank.jpg';
import bgQuestionScore from 'assets/images/backgrounds/question-type/bg-score.jpg';
import bgQuestionSingleChoice from 'assets/images/backgrounds/question-type/bg-single-choice.jpg';
import bgQuestionSliderScale from 'assets/images/backgrounds/question-type/bg-slider-scale.jpg';
import bgQuestionStarFull from 'assets/images/backgrounds/question-type/bg-star-full.jpg';
import bgQuestionStarHalf from 'assets/images/backgrounds/question-type/bg-star-half.jpg';
import bgQuestionTextboxRecommendMultiChoice from 'assets/images/backgrounds/question-type/bg-text-recommend-multi-choice.jpg';
import bgQuestionTextboxRecommend from 'assets/images/backgrounds/question-type/bg-text-recommend.jpg';
import CloseIcon from '@mui/icons-material/Close';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BrandLogo from 'common/components/brand-logo';
import BaseTextField from 'common/components/base-text-field';
import FormProvider from 'common/components/form-provider';
import { QuestionType } from 'pages/admin/campaign-overview/constants';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { createStyleSheet, handleError } from 'utils/helpers';
import * as yup from 'yup';
import ModalShareSurvey from '../components/modal-share-survey';
import ViewMode from '../components/view-mode';
import QuestionCheckbox from '../question/question-checkbox';
import QuestionDate from '../question/question-date';
import QuestionDropdown from '../question/question-dropdown';
import QuestionEmoji from '../question/question-emoji';
import QuestionImage from '../question/question-image';
import QuestionImageWithTitle from '../question/question-image-with-title';
import QuestionMatrixCheckbox from '../question/question-matrix-checkbox';
import QuestionMatrixRadio from '../question/question-matrix-radio';
import QuestionMatrixTextbox from '../question/question-matrix-textbox';
import QuestionNumber from '../question/question-number';
import QuestionRank from '../question/question-rank';
import QuestionScore from '../question/question-score';
import QuestionScoreStar from '../question/question-score-star';
import QuestionSingleShoice from '../question/question-single-choice';
import QuestionSliderScale from '../question/question-slider-scale';
import AnswerQuestionTextbox from '../question/question-textbox';
import QuestionTextboxRecommend from '../question/question-textbox-recommend';
import QuestionTextboxRecommendMultiChoice from '../question/question-textbox-recommend-multi-choice';
import { colors } from 'common/constants';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hook';
import {
    selectSurveyImage,
    selectWelcomeTextColor,
} from 'common/theme-selectors';
import {
    sortMatrixComponent,
    sortByField,
    closeSurvey,
} from 'utils/preview_helpers';
import lodash from 'lodash';
import { captureTimeByQuestion } from 'slices/previewSurveySlice';
import { createAnswers } from 'services/previewCampaign.service';
import { setIsLoading } from 'slices/loadingSlice';
import {
    IQuestionPreview,
    IQuestionBranch,
    IQuestionScreening,
    IQuestionLogicSkip,
} from 'types/previewCampaign';

const fileMaxSize = 20;
const fileMaxSizeInByte = fileMaxSize * 1024 * 1024;
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

const textRequire = 'Vui lòng trả lời câu hỏi này';

const StyledEnterIcon = styled(SubdirectoryArrowLeftIcon)({
    color: '#00000080',
    fontSize: 32,
});

const CHOOSE = 1;
// const NOT_CHOOSE = 0;

const imageYupPreparation = (question: IQuestionPreview) => {
    let baseAnswerYup = yup
        .mixed()
        .test(
            'fileType',
            'Ảnh không đúng định dạng, vui lòng tải ảnh có định dạng sau: .png, .jpg, .jpeg ',
            (value) => {
                if (!value) return true;
                const isValidType =
                    value instanceof File &&
                    SUPPORTED_FORMATS.includes(value.type);

                return isValidType;
            }
        )
        .test(
            'fileSize',
            `File không được vượt quá ${fileMaxSize}MB`,
            (value) => {
                if (!value) return true;
                return value instanceof File && value.size <= fileMaxSizeInByte;
            }
        );
    if (question.required) {
        baseAnswerYup = baseAnswerYup.test(
            'is-required',
            textRequire,
            (value) => {
                return value instanceof File;
            }
        );
    }
    return yup.object({
        answers: baseAnswerYup,
        questionId: yup.number(),
    });
};

const imageWithTitleYupPreparation = (question: IQuestionPreview) => {
    const baseAnswersYup = yup
        .array()
        .of(
            yup.object({
                image: yup
                    .mixed()
                    .nullable()
                    .test(
                        'fileType',
                        'Ảnh không đúng định dạng, vui lòng tải ảnh có định dạng sau: .png, .jpg, .jpeg ',
                        (file) => {
                            if (!file) return true;
                            return (
                                file instanceof File &&
                                SUPPORTED_FORMATS.includes(file.type)
                            );
                        }
                    )
                    .test(
                        'fileSize',
                        `File không được vượt quá ${fileMaxSize}MB`,
                        (file) => {
                            if (!(file instanceof File)) return true;
                            return file.size <= fileMaxSizeInByte;
                        }
                    ),
            })
        )
        .test('has-image-if-required', textRequire, (value) => {
            if (!question.required) return true;
            return (
                Array.isArray(value) &&
                value.some((item) => item?.image instanceof File)
            );
        });

    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
    });
};

const matrixCheckBoxYupPreparation = (question: IQuestionPreview) => {
    // const { minChoice, maxChoice } = question.logics || {};
    let baseAnswersYup = yup.array().of(yup.array().of(yup.boolean()));
    if (question.required) {
        baseAnswersYup = baseAnswersYup.test(
            'is-required',
            textRequire,
            (value) => {
                // check if value is an array and each item is an array has aleast 1 boolean true
                return (
                    Array.isArray(value) &&
                    value?.every((item) =>
                        item?.some((subItem) => subItem === true)
                    )
                );
            }
        );
        // if (minChoice) {
        //     baseAnswersYup = baseAnswersYup.test(
        //         'min-choice',
        //         `Chọn tối thiểu ${minChoice} lựa chọn mỗi hàng`,
        //         (value) => {
        //             // check if value is an array and each item is an array has aleast minChoice boolean true
        //             return (
        //                 Array.isArray(value) &&
        //                 value?.every(
        //                     (item) =>
        //                         item &&
        //                         item?.filter((subItem) => subItem === true)
        //                             .length >= (minChoice || 0)
        //                 )
        //             );
        //         }
        //     );
        // }
        // if (maxChoice) {
        //     baseAnswersYup = baseAnswersYup.test(
        //         'max-choice',
        //         `Chọn tối đa ${maxChoice} lựa chọn mỗi hàng`,
        //         (value) => {
        //             // check if value is an array and each item is an array has aleast maxChoice boolean true
        //             return (
        //                 Array.isArray(value) &&
        //                 value?.every(
        //                     (item) =>
        //                         item &&
        //                         item?.filter((subItem) => subItem === true)
        //                             .length <= (maxChoice || 1)
        //                 )
        //             );
        //         }
        //     );
        // }
    }
    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
    });
};

const checkBoxQuestionYupPreparation = (question: IQuestionPreview) => {
    const { minChoice, maxChoice } = question.logics || {};
    let baseAnswersYup = yup
        .array()
        .of(yup.number())
        .test(
            'noAnswerOrOtherAnswers',
            'Không được chọn cùng lúc "Không có đáp án nào" và các đáp án trên đó',
            (value) => {
                const listOptionTypeInValue: number[] = [];
                if (!value) return true;
                if (value.length === 0) return true;
                value.forEach((item) => {
                    const option = question.answers.find(
                        (answer: any) => answer.id === item // eslint-disable-line @typescript-eslint/no-explicit-any
                    );
                    if (option) {
                        listOptionTypeInValue.push(option.type);
                    } else {
                        listOptionTypeInValue.push(0);
                    }
                });
                // if listOptionTypeInValue include 2 then it can not include 1 or 0
                if (listOptionTypeInValue.includes(2)) {
                    if (
                        listOptionTypeInValue.includes(1) ||
                        listOptionTypeInValue.includes(0)
                    ) {
                        return false;
                    }
                }
                return true;
            }
        );
    if (question.required) {
        baseAnswersYup = baseAnswersYup.test(
            'is-required',
            textRequire,
            (value) => {
                return value && value?.length > 0;
            }
        );
    }
    if (minChoice) {
        baseAnswersYup = baseAnswersYup.test(
            'min-choice',
            `Chọn tối thiểu ${minChoice} đáp án`,
            (value) => {
                if (!value) return true;
                if (value.length === 0) return true;

                return value && value?.length >= (minChoice || 0);
            }
        );
    }
    if (maxChoice) {
        baseAnswersYup = baseAnswersYup.test(
            'max-choice',
            `Chọn tối đa ${maxChoice} đáp án`,
            (value) => {
                if (!value) return true;
                if (value.length === 0) return true;

                return value && value?.length <= (maxChoice || 1);
            }
        );
    }
    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
        answersOtherText: yup.string().when('answers', {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            is: (val: any) => {
                const otherOption = question.answers.find(
                    (item: any) => item.type === 1 // eslint-disable-line @typescript-eslint/no-explicit-any
                );
                if (!otherOption) return false;
                return val.includes(otherOption.id);
            },
            then: (schema) => schema.required('Vui lòng nhập'),
        }),
    });
};

interface IAnswerTheSurveyProps {
    setSurveyStep?: (step: number) => void;
}
const AnswerTheSurvey: React.FC<IAnswerTheSurveyProps> = ({
    setSurveyStep,
}) => {
    const dispatch = useAppDispatch();
    const isSubmittingRef = useRef(false);
    const [currentQuestion, setCurrentQuestion] =
        React.useState<IQuestionPreview>();
    const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
    const campaignId = useParams<{ id: string }>().id;
    const me = useAppSelector((state) => state.previewSurvey.me);
    const surveyImage = useAppSelector(selectSurveyImage);
    const welcomeTextColor = useAppSelector(selectWelcomeTextColor);
    const questions = useAppSelector((state) => state.previewSurvey.questions);
    const geoCoords = useAppSelector((state) => state.geoLocation.coords);
    const captureTimeStamp = useAppSelector(
        (state) => state.previewSurvey.capturePerformanceTimeByQuestion
    );
    const surveyState = useAppSelector(
        (state) => state.previewSurvey.surveyState
    );
    const [displayedBaseComponents, setDisplayedBaseComponents] =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.useState<Record<number, any>>({});

    // Track which questions have been visited/shown to the user
    const [visitedQuestionIds, setVisitedQuestionIds] = React.useState<
        Set<number>
    >(new Set());

    const backgroundWithQuestionType = {
        [QuestionType.Textbox]: bgQuestionTextbox,
        [QuestionType.SingleChoice]: bgQuestionSingleChoice,
        [QuestionType.Checkbox]: bgQuestionCheckbox,
        [QuestionType.Score]: bgQuestionScore,
        [QuestionType.ScoreStarFull]: bgQuestionStarFull,
        [QuestionType.ScoreStarHalf]: bgQuestionStarHalf,
        [QuestionType.SliderScale]: bgQuestionSliderScale,
        [QuestionType.Rank]: bgQuestionRank,
        [QuestionType.MatrixCheckbox]: bgQuestionMatrixCheckbox,
        [QuestionType.MatrixRadio]: bgQuestionMatrixRadio,
        [QuestionType.MatrixText]: bgQuestionMatrixTextbox,
        [QuestionType.Dropdown]: bgQuestionDropdown,
        [QuestionType.Number]: bgQuestionNumber,
        [QuestionType.TextboxRecommend]: bgQuestionTextboxRecommend,
        [QuestionType.TextboxRecommendMultiChoice]:
            bgQuestionTextboxRecommendMultiChoice,
        [QuestionType.Date]: bgQuestionDate,
        [QuestionType.Image]: bgQuestionScore,
        [QuestionType.ImageWithTitle]: bgQuestionStarFull,
        [QuestionType.Emoji]: bgQuestionEmoji,
    };

    const [internalClockTimeStamp, setInternalClockTimeStamp] =
        React.useState(0);

    useEffect(() => {
        setCurrentQuestion(questions[0]);
        setInternalClockTimeStamp(performance.now());
    }, [questions]);

    const createValidationSchemaAndDefaults = (
        questions: IQuestionPreview[]
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const shape: Record<string, yup.Schema<any>> = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defaultValues: Record<string, any> = {}; // Để lưu trữ defaultValues

        questions.forEach((question: IQuestionPreview) => {
            const questionKey = `question_${question.id}`;
            switch (question.type) {
                case QuestionType.Textbox:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .string()
                                  .required(textRequire)
                                  .test(
                                      'is-empty-string',
                                      textRequire,
                                      (value) => {
                                          return value?.trim() !== '';
                                      }
                                  )
                            : yup.string().nullable(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: '',
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Checkbox:
                    shape[questionKey] =
                        checkBoxQuestionYupPreparation(question);

                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                        answersOtherText: '',
                    };
                    break;
                case QuestionType.SingleChoice:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup.number().required(textRequire)
                            : yup.number().nullable(),
                        questionId: yup.number(),
                        answersOtherText: yup.string().when('answers', {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            is: (val: any) => {
                                const otherOption = question.answers.find(
                                    (item: any) => item.type === 1 // eslint-disable-line @typescript-eslint/no-explicit-any
                                );
                                if (!otherOption) return false;
                                return Number(val) === otherOption.id;
                            },
                            then: (schema) => schema.required('Vui lòng nhập'),
                        }),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                        answersOtherText: '',
                    };
                    break;
                case QuestionType.Score:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup.number().required(textRequire)
                            : yup.number().nullable(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.ScoreStarHalf:
                case QuestionType.ScoreStarFull:
                    shape[questionKey] = yup.object({
                        answers: yup.array().of(
                            yup.object({
                                star: question.required
                                    ? yup.number().required(textRequire)
                                    : yup.number().nullable(),
                                id: yup.number(),
                            })
                        ),
                        questionId: yup.number(),
                    });

                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                star: null,
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                case QuestionType.SliderScale:
                    shape[questionKey] = yup.object({
                        answers: yup.array().of(
                            yup.object({
                                score: question.required
                                    ? yup.number().required(textRequire)
                                    : yup.number().nullable(),
                                id: yup.number(),
                            })
                        ),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                score: 0,
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                case QuestionType.Rank:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup.array().of(
                                  yup.object({
                                      id: yup.number(),
                                      label: yup.string(),
                                      position: yup
                                          .number()
                                          .required(textRequire),
                                  })
                              )
                            : yup.array(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixCheckbox:
                    shape[questionKey] = matrixCheckBoxYupPreparation(question);
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixRadio:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .array()
                                  .of(yup.string())
                                  .test('is-required', textRequire, (value) => {
                                      // check if value is an array that contain 5 string
                                      return (
                                          Array.isArray(value) &&
                                          value?.every(
                                              (item) =>
                                                  typeof item === 'string' &&
                                                  (item as string).length > 0
                                          )
                                      );
                                  })
                            : yup.array(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixText:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .array()
                                  .of(yup.array().of(yup.string()))
                                  .test('is-required', textRequire, (value) => {
                                      // check if value is an array and each item is an array that contain all string not empty
                                      return (
                                          Array.isArray(value) &&
                                          value?.every((item) =>
                                              item?.every(
                                                  (subItem) =>
                                                      typeof subItem ===
                                                          'string' &&
                                                      subItem.length > 0
                                              )
                                          )
                                      );
                                  })
                            : yup.array(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Dropdown:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup.object().required(textRequire)
                            : yup.object().nullable(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Number:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .number()
                                  .typeError('Vui lòng nhập số')
                                  .required(textRequire)
                            : yup
                                  .number()
                                  .typeError('Vui lòng nhập số')
                                  .nullable(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.TextboxRecommend:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .object({
                                      label: yup.string(),
                                      value: yup.number(),
                                  })
                                  .required(textRequire)
                            : yup
                                  .object({
                                      label: yup.string(),
                                      value: yup.number(),
                                  })
                                  .nullable(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.TextboxRecommendMultiChoice:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .array()
                                  .test('is-required', textRequire, (value) => {
                                      return (
                                          Array.isArray(value) &&
                                          value.length > 0
                                      );
                                  })
                            : yup.array(),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Date:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .object({
                                      day: yup.string().nullable(),
                                      month: yup.string().nullable(),
                                      year: yup.string().nullable(),
                                  })
                                  .test('is-required', textRequire, (value) => {
                                      return !!(
                                          value &&
                                          value.day &&
                                          value.month &&
                                          value.year
                                      );
                                  })
                            : yup.object({
                                  day: yup.string().nullable(),
                                  month: yup.string().nullable(),
                                  year: yup.string().nullable(),
                              }),
                        questionId: yup.number(),
                    });

                    defaultValues[questionKey] = {
                        answers: {
                            day: '',
                            month: '',
                            year: '',
                        },
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Image:
                    shape[questionKey] = imageYupPreparation(question);
                    defaultValues[questionKey] = {
                        answers: '',
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.ImageWithTitle:
                    shape[questionKey] = imageWithTitleYupPreparation(question);
                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Emoji:
                    shape[questionKey] = yup.object({
                        answers: question.required
                            ? yup
                                  .array()
                                  .of(
                                      yup.object({
                                          emoji: yup.string().nullable(),
                                          id: yup.number(),
                                      })
                                  )
                                  .test('is-required', textRequire, (value) => {
                                      return (
                                          Array.isArray(value) &&
                                          value.every((item) => {
                                              return item.emoji !== '';
                                          })
                                      );
                                  })
                            : yup.array().of(
                                  yup.object({
                                      emoji: yup.string().nullable(),
                                      id: yup.number(),
                                  })
                              ),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                emoji: '',
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                default:
                    break;
            }
        });
        const validationSchema = yup.object().shape(shape);
        return { validationSchema, defaultValues };
    };

    const { validationSchema, defaultValues } = React.useMemo(() => {
        return createValidationSchemaAndDefaults(questions || []);
    }, [questions]);
    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
        mode: 'onChange',
    });

    useEffect(() => {
        const prevDefaultValues = JSON.stringify(methods.getValues());
        const newDefaultValues = JSON.stringify(defaultValues);

        if (prevDefaultValues !== newDefaultValues) {
            methods.reset(defaultValues);
        }
    }, [defaultValues, methods]);

    const [openModalShareSurvey, setOpenModalShareSurvey] =
        React.useState(false);

    const {
        watch,
        // setError,
        formState: { isSubmitting, errors },
    } = methods;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formatDataBeforeSubmit = (data: any) => {
        const result: Record<string, any> = {}; // eslint-disable-line @typescript-eslint/no-explicit-any
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const element = data[key];
                const questionId = key.split('_')[1];
                if (questionId) {
                    const findQuestion = questions.find(
                        (item) => item.id === Number(questionId)
                    );
                    if (findQuestion) {
                        const {
                            type,
                            answers: dataQuestion,
                            logics: questionLogics,
                        } = findQuestion;
                        if (type === QuestionType.MatrixCheckbox) {
                            const actualAnswer: string[] = [];
                            const questionDisplay =
                                displayedBaseComponents[findQuestion.id];
                            const displayRowsTitle = questionDisplay
                                ? questionDisplay.rowsTitle
                                : dataQuestion.rowsTitle;
                            const displayColumnsTitle = questionDisplay
                                ? questionDisplay.columnsTitle
                                : dataQuestion.columnsTitle;
                            console.log('MatrixCheckbox question display:', {
                                displayRowsTitle,
                                displayColumnsTitle,
                                elementAnswers: element.answers,
                            });
                            if (
                                element.answers &&
                                displayRowsTitle &&
                                displayColumnsTitle
                            ) {
                                displayRowsTitle.forEach(
                                    (rowTitle: string, rowIndex: number) => {
                                        displayColumnsTitle.forEach(
                                            (
                                                columnTitle: string,
                                                colIndex: number
                                            ) => {
                                                if (
                                                    (questionLogics?.matrixAnswerType ===
                                                        'row' &&
                                                        element.answers[
                                                            rowIndex
                                                        ] &&
                                                        element.answers[
                                                            rowIndex
                                                        ][colIndex]) ||
                                                    (questionLogics?.matrixAnswerType ===
                                                        'column' &&
                                                        element.answers[
                                                            colIndex
                                                        ] &&
                                                        element.answers[
                                                            colIndex
                                                        ][rowIndex])
                                                ) {
                                                    actualAnswer.push(
                                                        `${rowTitle}&-&${columnTitle}`
                                                    );
                                                }
                                            }
                                        );
                                    }
                                );
                            }
                            element.answers = actualAnswer;
                        }
                        if (type === QuestionType.MatrixText) {
                            const { rowsTitle } = dataQuestion;
                            if (
                                element.answers &&
                                Array.isArray(element.answers) &&
                                rowsTitle
                            ) {
                                element.answers = element.answers.map(
                                    (row: string[], rowIndex: number) => {
                                        if (Array.isArray(row)) {
                                            row.unshift(rowsTitle[rowIndex]);
                                        }
                                        return row;
                                    }
                                );
                            }
                        }
                        if (type === QuestionType.Number) {
                            element.answers = (
                                element.answers || ''
                            ).toString();
                        }
                        if (type === QuestionType.ImageWithTitle) {
                            if (
                                findQuestion.answers &&
                                findQuestion.answers.images &&
                                element.answers &&
                                Array.isArray(element.answers)
                            ) {
                                const optionImages =
                                    findQuestion.answers.images;
                                element.answers = element.answers.map(
                                    (
                                        item: { image: File },
                                        itemIndex: number
                                    ) => {
                                        return {
                                            image: item.image,
                                            option_id:
                                                optionImages[itemIndex]?.value,
                                        };
                                    }
                                );
                            }
                        }
                        result[questionId] = element;
                    }
                }
            }
        }
        return result;
    };

    // Extract submission logic to bypass validation when needed (e.g., screening)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submitSurvey = async (data: any, skipValidation = false) => {
        if (isSubmittingRef.current) {
            return;
        }

        // If not skipping validation, validate only visited questions
        if (!skipValidation) {
            const questionsToValidate = Array.from(visitedQuestionIds).map(
                (id) => `question_${id}`
            );
            const validationResults = await Promise.all(
                questionsToValidate.map((fieldName) =>
                    methods.trigger(fieldName)
                )
            );

            // If any visited question fails validation, don't submit
            if (validationResults.some((result) => !result)) {
                isSubmittingRef.current = false;
                return;
            }
        }

        isSubmittingRef.current = true;
        const timeStampPayload = { ...captureTimeStamp };
        const finalQuestionId = currentQuestion?.id.toString() || '';
        const finalTimeCapture = {
            questionId: finalQuestionId,
            time: performance.now() - internalClockTimeStamp,
        };
        if (timeStampPayload[finalQuestionId]) {
            timeStampPayload[finalQuestionId] += finalTimeCapture.time;
        } else {
            timeStampPayload[finalQuestionId] = finalTimeCapture.time;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const quotas: any[] = [];
        const quotaAnswers: number[] = [];

        questions.forEach((q) => {
            if (
                q.type === QuestionType.Dropdown ||
                q.type === QuestionType.SingleChoice
            ) {
                const questionKey = `question_${q.id}`;
                const answer = data[questionKey]?.answers;

                if (!answer) return;

                const answerId =
                    q.type === QuestionType.Dropdown ? answer?.value : answer;

                if (answerId) {
                    quotaAnswers.push(Number(answerId));
                }
            }
        });

        if (quotaAnswers.length > 0) {
            quotas.push({
                answers: quotaAnswers,
            });
        }

        const formatData = formatDataBeforeSubmit(data);

        try {
            dispatch(setIsLoading(true));
            const res = await createAnswers(
                campaignId ?? '',
                formatData,
                timeStampPayload,
                quotas.length > 0 ? quotas : undefined,
                geoCoords
            );

            if (res.success) {
                setSurveyStep && setSurveyStep(2);
                dispatch(setIsLoading(false));
                toast.success(res.message);
                isSubmittingRef.current = false;
            } else {
                dispatch(setIsLoading(false));
                isSubmittingRef.current = false;
                if (res.status_code === 3201) {
                    setSurveyStep && setSurveyStep(3);
                } else {
                    toast.error(res.message);
                }
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            isSubmittingRef.current = false;
            handleError(error);
        }
    };

    // Form onSubmit handler (used by FormProvider)
    const onSubmit = () => {
        // Submission is handled by handleNextQuestion
    };

    const determineQuestionRadioSkipLogic = (
        logic: IQuestionLogicSkip | IQuestionScreening | IQuestionBranch
    ) => {
        const currentValues = methods.getValues();
        const questionAnswer = currentValues[`question_${logic.question_id}`];
        const answerId = questionAnswer?.answers;
        const { condition, answer_id: logicAnswerId } = logic;
        if (condition === CHOOSE) {
            if (Number(answerId) === logicAnswerId) {
                return true;
            }
            return false;
        } else {
            if (Number(answerId) !== logicAnswerId) {
                return true;
            }
            return false;
        }
    };
    const determineQuestionCheckboxSkipLogic = (
        logic: IQuestionLogicSkip | IQuestionScreening | IQuestionBranch
    ) => {
        const currentValues = methods.getValues();
        const questionAnswer = currentValues[`question_${logic.question_id}`];
        const { answers } = questionAnswer || {};
        const { answer_id: logicAnswerId, condition } = logic;
        if (condition === CHOOSE) {
            if (
                answers &&
                answers.length > 0 &&
                answers.includes(logicAnswerId)
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            if (!answers || answers.length === 0) {
                return true;
            } else if (
                answers &&
                answers.length > 0 &&
                !answers.includes(logicAnswerId)
            ) {
                return true;
            } else {
                return false;
            }
        }
    };
    const determineQuestionMatrixCheckboxSkipLogic = (
        logic: IQuestionLogicSkip | IQuestionScreening
    ) => {
        const currentValues = methods.getValues();
        const questionAnswer = currentValues[`question_${logic.question_id}`];
        const { answers } = questionAnswer || {};
        const { answer_id: logicAnswerId, condition, question_id } = logic;
        const findQuestion = questions.find((item) => item.id === question_id);
        if (!findQuestion) {
            return false;
        }
        const { logics: questionLogics, answers: dataQuestion } = findQuestion;
        const actualAnswer: string[] = [];
        const { rowsTitle, columnsTitle } = dataQuestion;
        const displayRowsTitle =
            questionLogics?.matrixAnswerType === 'row'
                ? rowsTitle
                : columnsTitle;
        const displayColumnsTitle =
            questionLogics?.matrixAnswerType === 'row'
                ? columnsTitle
                : rowsTitle;
        displayRowsTitle.forEach((rowTitle: string, rowIndex: number) => {
            displayColumnsTitle.forEach(
                (columnTitle: string, colIndex: number) => {
                    if (answers[rowIndex] && answers[rowIndex][colIndex]) {
                        actualAnswer.push(
                            questionLogics?.matrixAnswerType === 'row'
                                ? `${rowTitle}&-&${columnTitle}`
                                : `${columnTitle}&-&${rowTitle}`
                        );
                    }
                }
            );
        });
        if (condition === CHOOSE) {
            if (actualAnswer.includes(String(logicAnswerId))) {
                return true;
            } else {
                return false;
            }
        } else {
            if (!actualAnswer.includes(String(logicAnswerId))) {
                return true;
            } else {
                return false;
            }
        }
    };
    const determineQuestionMatrixRadioSkipLogic = (
        logic: IQuestionLogicSkip | IQuestionScreening
    ) => {
        const currentValues = methods.getValues();
        const questionAnswer = currentValues[`question_${logic.question_id}`];
        const { answers } = questionAnswer || {};
        const { answer_id: logicAnswerId, condition, question_id } = logic;
        const findQuestion = questions.find((item) => item.id === question_id);
        if (!findQuestion) {
            return false;
        }
        if (condition === CHOOSE) {
            if (answers.includes(String(logicAnswerId))) {
                return true;
            } else {
                return false;
            }
        } else {
            if (!answers.includes(String(logicAnswerId))) {
                return true;
            } else {
                return false;
            }
        }
    };
    const determineQuestionDropDownSkipLogic = (
        logic: IQuestionLogicSkip | IQuestionScreening | IQuestionBranch
    ) => {
        const currentValues = methods.getValues();
        const questionAnswer = currentValues[`question_${logic.question_id}`];
        const { answers } = questionAnswer || {};
        const { answer_id: logicAnswerId, condition } = logic;
        if (condition === CHOOSE) {
            if (!answers) {
                return false;
            } else if (answers && answers.value) {
                if (answers.value === logicAnswerId) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        } else {
            if (!answers) {
                return true;
            } else if (answers && answers.value) {
                if (answers.value !== logicAnswerId) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        }
    };

    const checkLogicSkip = (nextQuestionIndex: number): number => {
        const nextQuestion = questions[nextQuestionIndex];
        if (nextQuestion) {
            const { skipLogic } = nextQuestion;
            if (skipLogic && skipLogic.length > 0) {
                let checkBreak = false;
                skipLogic.forEach((item: IQuestionLogicSkip) => {
                    switch (item.question_kind) {
                        case QuestionType.SingleChoice:
                            if (determineQuestionRadioSkipLogic(item))
                                checkBreak = true;
                            break;
                        case QuestionType.Checkbox:
                            if (determineQuestionCheckboxSkipLogic(item))
                                checkBreak = true;
                            break;
                        case QuestionType.MatrixCheckbox:
                            if (determineQuestionMatrixCheckboxSkipLogic(item))
                                checkBreak = true;
                            break;
                        case QuestionType.MatrixRadio:
                            if (determineQuestionMatrixRadioSkipLogic(item))
                                checkBreak = true;
                            break;
                        case QuestionType.Dropdown:
                            if (determineQuestionDropDownSkipLogic(item))
                                checkBreak = true;
                            break;
                        default:
                            break;
                    }
                    if (checkBreak) {
                        return;
                    }
                });
                if (checkBreak) {
                    return checkLogicSkip(nextQuestionIndex + 1);
                } else {
                    return nextQuestionIndex;
                }
            } else {
                return nextQuestionIndex;
            }
        } else {
            return nextQuestionIndex;
        }
    };

    const checkScreening = (question: IQuestionPreview | undefined) => {
        if (!question) {
            return false;
        }
        if (question.creeningLogic && question.creeningLogic.length > 0) {
            switch (question.type) {
                case QuestionType.Checkbox:
                    return question.creeningLogic.some(
                        (item: IQuestionScreening) => {
                            return determineQuestionCheckboxSkipLogic(item);
                        }
                    );
                case QuestionType.SingleChoice:
                    return question.creeningLogic.some(
                        (item: IQuestionScreening) => {
                            return determineQuestionRadioSkipLogic(item);
                        }
                    );
                case QuestionType.MatrixCheckbox:
                    return question.creeningLogic.some(
                        (item: IQuestionScreening) => {
                            return determineQuestionMatrixCheckboxSkipLogic(
                                item
                            );
                        }
                    );
                case QuestionType.MatrixRadio:
                    return question.creeningLogic.some(
                        (item: IQuestionScreening) => {
                            return determineQuestionMatrixRadioSkipLogic(item);
                        }
                    );
                case QuestionType.Dropdown:
                    return question.creeningLogic.some(
                        (item: IQuestionScreening) => {
                            return determineQuestionDropDownSkipLogic(item);
                        }
                    );
                default:
                    return false;
            }
        }

        return false;
    };

    const checkLogicBranch = (nextQuestionIndex: number): number => {
        if (
            currentQuestion?.branchLogic &&
            currentQuestion?.branchLogic.length > 0
        ) {
            let jumpQuestionId: number | null = null;
            currentQuestion.branchLogic.forEach((item: IQuestionBranch) => {
                switch (currentQuestion.type) {
                    case QuestionType.SingleChoice:
                        if (determineQuestionRadioSkipLogic(item)) {
                            jumpQuestionId = item.branch_question_id;
                            return;
                        }
                        break;
                    case QuestionType.Checkbox:
                        if (determineQuestionCheckboxSkipLogic(item)) {
                            jumpQuestionId = item.branch_question_id;
                            return;
                        }
                        break;
                    case QuestionType.Dropdown:
                        if (determineQuestionDropDownSkipLogic(item)) {
                            jumpQuestionId = item.branch_question_id;
                            return;
                        }
                        break;
                    default:
                        return;
                }
            });
            if (!jumpQuestionId) return nextQuestionIndex;
            const findJumpQuestionIndex = questions.findIndex(
                (item) => item.id === jumpQuestionId
            );
            if (findJumpQuestionIndex < 0) return nextQuestionIndex;
            return findJumpQuestionIndex;
        }
        return nextQuestionIndex;
    };

    // check logic display answer on title
    const checkLogicDisplayAnswerOnTitle = useCallback(
        (nextQuestion: IQuestionPreview) => {
            if (nextQuestion.questionTitleLogic?.id) {
                const {
                    mention_question_id,
                    // start_pos,
                    // end_pos
                } = nextQuestion.questionTitleLogic;
                const currentValues = methods.getValues();
                const findValue =
                    currentValues[`question_${mention_question_id}`];
                if (findValue && findValue.answers) {
                    const answerId =
                        typeof findValue.answers === 'string'
                            ? Number(findValue.answers)
                            : findValue.answers.value;
                    const findAnswerText = questions
                        .find((itemQ) => itemQ.id === mention_question_id)
                        ?.answers.find(
                            (itemA: { id: number }) => itemA.id === answerId
                        );
                    if (findAnswerText && findAnswerText.label) {
                        const questionTitle = nextQuestion.question.replace(
                            '$title',
                            findAnswerText.label
                        );
                        return {
                            ...nextQuestion,
                            question: questionTitle,
                        };
                    } else {
                        return nextQuestion;
                    }
                } else {
                    return nextQuestion;
                }
            } else {
                return nextQuestion;
            }
        },
        [methods, questions]
    );

    const orderFetchBaseAnswers = useCallback(
        <T extends Record<string, any>>( // eslint-disable-line
            answers: T[],
            orderBy: string,
            orderField: keyof T
        ) => {
            return sortByField(answers, orderBy, orderField.toString());
        },
        []
    );

    const fetchBaseOptions = useCallback(
        (
            question: IQuestionPreview
        ): {
            id: number;
            label: string;
            type?: number;
            image?: string;
            position?: number;
        }[] => {
            const { question_id } = question.answerFromOtherQuestionLogic || {};
            const currentValues = methods.getValues();
            let questionAnswer = currentValues[`question_${question_id}`];
            let findQuestionBase = questions.find(
                (item) => item.id === question_id
            );
            if (!findQuestionBase) {
                return [];
            }
            if (findQuestionBase.type === QuestionType.Checkbox) {
                questionAnswer = questionAnswer?.answers;
            } else if (
                findQuestionBase.type ===
                QuestionType.TextboxRecommendMultiChoice
            ) {
                questionAnswer = questionAnswer?.answers?.map(
                    (item: { value: number }) => item.value
                );
            }
            while (
                findQuestionBase &&
                findQuestionBase.answerFromOtherQuestionLogic?.id
            ) {
                const {
                    question_id: find_parent_question_id,
                }: { question_id: number } =
                    findQuestionBase.answerFromOtherQuestionLogic;
                findQuestionBase = questions.find(
                    (item) => item.id === find_parent_question_id
                );
            }
            const answerBase = findQuestionBase?.answers;
            const answersOtherText =
                currentValues[`question_${question_id}`]?.answersOtherText;
            switch (findQuestionBase?.type) {
                case QuestionType.Checkbox:
                    switch (question.type) {
                        case QuestionType.Checkbox:
                        case QuestionType.SingleChoice: {
                            const listAnswerFilter = answerBase.filter(
                                (
                                    item: any // eslint-disable-line @typescript-eslint/no-explicit-any
                                ) =>
                                    Array.isArray(questionAnswer) &&
                                    questionAnswer.includes(item.id) &&
                                    item.type !== 2
                            );

                            const finalAnswers = listAnswerFilter.map(
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                (item: any) => {
                                    if (item.type === 1) {
                                        return {
                                            ...item,
                                            label: answersOtherText,
                                        };
                                    }
                                    return item;
                                }
                            );
                            return orderFetchBaseAnswers(
                                finalAnswers,
                                question.logics?.orderBy || '',
                                'label'
                            );
                        }

                        case QuestionType.Dropdown:
                        case QuestionType.TextboxRecommend:
                        case QuestionType.TextboxRecommendMultiChoice:
                            return orderFetchBaseAnswers(
                                answerBase.filter(
                                    (item: {
                                        id: number;
                                        label: string;
                                        image: string;
                                    }) =>
                                        Array.isArray(questionAnswer) &&
                                        questionAnswer.includes(item.id)
                                ),
                                question.logics?.orderBy || '',
                                'label'
                            );
                        case QuestionType.Rank:
                            return orderFetchBaseAnswers(
                                answerBase.filter(
                                    (
                                        item: any // eslint-disable-line @typescript-eslint/no-explicit-any
                                    ) =>
                                        item.type !== 1 &&
                                        Array.isArray(questionAnswer) &&
                                        questionAnswer.includes(item.id)
                                ),
                                question.logics?.orderBy || '',
                                'label'
                            ).map(
                                (
                                    item: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                    index
                                ) => ({
                                    id: item.id,
                                    label: item.label,
                                    position: index + 1,
                                })
                            );
                        default:
                            return [];
                    }
                case QuestionType.TextboxRecommendMultiChoice:
                    switch (question.type) {
                        case QuestionType.Checkbox:
                        case QuestionType.SingleChoice:
                        case QuestionType.Dropdown:
                        case QuestionType.TextboxRecommend:
                        case QuestionType.TextboxRecommendMultiChoice:
                            return orderFetchBaseAnswers(
                                answerBase.filter(
                                    (item: {
                                        id: number;
                                        label: string;
                                        image: string;
                                    }) =>
                                        Array.isArray(questionAnswer) &&
                                        questionAnswer.includes(item.id)
                                ),
                                question.logics?.orderBy || '',
                                'label'
                            );
                        case QuestionType.Rank:
                            return orderFetchBaseAnswers(
                                answerBase.filter(
                                    (item: {
                                        id: number;
                                        label: string;
                                        image: string;
                                    }) =>
                                        Array.isArray(questionAnswer) &&
                                        questionAnswer.includes(item.id)
                                ),
                                question.logics?.orderBy || '',
                                'label'
                            ).map(
                                (
                                    item: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                                    index
                                ) => ({
                                    id: item.id,
                                    label: item.label,
                                    position: index + 1,
                                })
                            );
                        default:
                            return [];
                    }
                default:
                    return [];
            }
        },
        [methods, questions, orderFetchBaseAnswers]
    );

    const fetchBaseComponent = useCallback(
        (
            componentType: string,
            question_id: number,
            question: IQuestionPreview
        ) => {
            const currentValues = methods.getValues();
            const questionAnswer = currentValues[`question_${question_id}`];
            let findQuestionBase = questions.find(
                (item) => item.id === question_id
            );
            while (
                findQuestionBase &&
                findQuestionBase.answerFromOtherQuestionLogic?.id
            ) {
                const {
                    question_id: find_parent_question_id,
                }: { question_id: number } =
                    findQuestionBase.answerFromOtherQuestionLogic;
                findQuestionBase = questions.find(
                    (item) => item.id === find_parent_question_id
                );
            }
            if (!findQuestionBase) {
                if (componentType === 'row') {
                    return question.answers.rowsTitle || [];
                } else {
                    return question.answers.columnsTitle || [];
                }
            }
            if (findQuestionBase.type === QuestionType.Checkbox) {
                return findQuestionBase.answers
                    .filter(
                        (item: { id: number; label: string; image: string }) =>
                            questionAnswer?.answers?.includes(item.id)
                    )
                    .map(
                        (item: { id: number; label: string; image: string }) =>
                            item.label
                    );
            } else if (
                findQuestionBase.type ===
                QuestionType.TextboxRecommendMultiChoice
            ) {
                return findQuestionBase.answers
                    .filter((item: { id: number; label: string }) =>
                        questionAnswer?.answers
                            ?.map((item: { value: number }) => item.value)
                            .includes(item.id)
                    )
                    .map((item: { id: number; label: string }) => item.label);
            } else {
                return [];
            }
        },
        [methods, questions]
    );

    const fetchBaseComponents = useCallback(
        (question: IQuestionPreview) => {
            const { row_question_id = 0, column_question_id = 0 } =
                question.answerFromOtherQuestionLogic || {};
            const rowData = fetchBaseComponent(
                'row',
                row_question_id,
                question
            );
            const columnData = fetchBaseComponent(
                'column',
                column_question_id,
                question
            );
            const { rowsTitle, columnsTitle } = sortMatrixComponent(
                rowData,
                columnData,
                question.logics?.orderBy || ''
            );
            let matrixValue: any[] = []; // eslint-disable-line
            const matrixAnswerType = question.logics?.matrixAnswerType || '';
            if (question.type === QuestionType.MatrixText) {
                matrixValue = rowsTitle.map(() => columnsTitle.map(() => ''));
            } else if (question.type === QuestionType.MatrixCheckbox) {
                matrixValue =
                    matrixAnswerType === 'row'
                        ? rowsTitle.map(() => columnsTitle.map(() => false))
                        : columnsTitle.map(() => rowsTitle.map(() => false));
            } else if (question.type === QuestionType.MatrixRadio) {
                matrixValue =
                    matrixAnswerType === 'row'
                        ? rowsTitle.map(() => '')
                        : columnsTitle.map(() => '');
            }
            if (question.type === QuestionType.MatrixText) {
                return {
                    rowsTitle,
                    columnsTitle,
                    matrix: matrixValue,
                };
            }
            return {
                rowsTitle: rowsTitle,
                columnsTitle: columnsTitle,
                matrix: matrixValue,
            };
        },
        [fetchBaseComponent]
    );

    // check answerFromOtherQuestionLogic
    const checkAnswerFromOtherQuestionLogic = useCallback(
        (nextQuestion: IQuestionPreview) => {
            if (nextQuestion.answerFromOtherQuestionLogic?.id) {
                let baseOptions: any[] = []; // eslint-disable-line
                let baseComponents: any = () => {}; // eslint-disable-line
                switch (nextQuestion.type) {
                    case QuestionType.SingleChoice:
                    case QuestionType.Checkbox:
                    case QuestionType.Dropdown:
                    case QuestionType.TextboxRecommend:
                    case QuestionType.TextboxRecommendMultiChoice:
                    case QuestionType.Rank:
                        baseOptions = fetchBaseOptions(nextQuestion);
                        return {
                            ...nextQuestion,
                            answers: baseOptions.concat(nextQuestion.answers),
                        };
                    case QuestionType.MatrixCheckbox:
                    case QuestionType.MatrixRadio:
                    case QuestionType.MatrixText:
                        baseComponents = fetchBaseComponents(nextQuestion);
                        setDisplayedBaseComponents((prev) => ({
                            ...prev,
                            [nextQuestion.id]: baseComponents,
                        }));
                        return {
                            ...nextQuestion,
                            answers: baseComponents,
                        };
                    default:
                        return nextQuestion;
                }
            } else {
                return nextQuestion;
            }
        },
        [fetchBaseOptions, fetchBaseComponents]
    );

    useEffect(() => {
        let nextQuestion = questions?.[currentQuestionIndex];
        if (nextQuestion) {
            nextQuestion = checkLogicDisplayAnswerOnTitle(nextQuestion);
            nextQuestion = checkAnswerFromOtherQuestionLogic(nextQuestion);
            setCurrentQuestion(nextQuestion);

            // Track this question as visited
            setVisitedQuestionIds((prev) => new Set(prev.add(nextQuestion.id)));
        }
    }, [
        currentQuestionIndex,
        questions,
        checkLogicDisplayAnswerOnTitle,
        checkAnswerFromOtherQuestionLogic,
    ]);

    const totalQuestions = questions?.length || 0;
    const [history, setHistory] = React.useState<number[]>([]);

    const handlePreviousQuestion = () => {
        if (history.length > 0) {
            dispatch(
                captureTimeByQuestion({
                    questionId: currentQuestion?.id.toString() || '',
                    time: performance.now() - internalClockTimeStamp,
                })
            );
            setInternalClockTimeStamp(performance.now());
            const lastIndex = history[history.length - 1];
            setHistory((prevHistory) => prevHistory.slice(0, -1));
            setCurrentQuestionIndex(lastIndex);
        }
    };
    const handleNextQuestion = async () => {
        // log answers so far for validation and submission
        console.log(
            'Current form values on Next:',
            methods.getValues()['question_8073']
        );
        if (isSubmittingRef.current) {
            return;
        }
        const isValid = await methods.trigger(
            `question_${currentQuestion?.id}`
        );

        if (!isValid) {
            return;
        }

        // Screening
        if (checkScreening(currentQuestion)) {
            // Bypass validation by calling submitSurvey directly with current form values
            await submitSurvey(methods.getValues(), true);
            return;
        }
        if (currentQuestionIndex === totalQuestions - 1) {
            // Submit without re-validation (current question already validated above)
            await submitSurvey(methods.getValues(), true);
            return;
        }
        // skip
        let nextQuestionIndex = currentQuestionIndex + 1;
        nextQuestionIndex = checkLogicSkip(nextQuestionIndex);
        // branching
        nextQuestionIndex = checkLogicBranch(nextQuestionIndex);

        if (nextQuestionIndex > totalQuestions - 1) {
            // Submit without re-validation (current question already validated above)
            await submitSurvey(methods.getValues(), true);
            return;
        }
        dispatch(
            captureTimeByQuestion({
                questionId: currentQuestion?.id.toString() || '',
                time: performance.now() - internalClockTimeStamp,
            })
        );
        setInternalClockTimeStamp(performance.now());
        setHistory((prevHistory) => [...prevHistory, currentQuestionIndex]);
        setCurrentQuestionIndex(
            Math.min(nextQuestionIndex, totalQuestions - 1)
        );
    };

    const renderQuestion = useCallback(() => {
        const questionKey = `question_${currentQuestion?.id}`;
        switch (currentQuestion?.type) {
            case QuestionType.Textbox:
                return (
                    <AnswerQuestionTextbox
                        key={questionKey}
                        name={`${questionKey}.answers`}
                    />
                );
            case QuestionType.Checkbox:
                return (
                    <QuestionCheckbox
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={
                            Array.isArray(currentQuestion?.answers)
                                ? currentQuestion?.answers?.map((item) => ({
                                      label: item.label,
                                      value: item.id,
                                      image: item.image,
                                      type: item.type,
                                  }))
                                : []
                        }
                    />
                );
            case QuestionType.SingleChoice:
                return (
                    <QuestionSingleShoice
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={
                            Array.isArray(currentQuestion?.answers)
                                ? currentQuestion?.answers?.map((item) => ({
                                      label: item.label,
                                      value: item.id,
                                      image: item.image,
                                      type: item.type,
                                  }))
                                : []
                        }
                    />
                );
            case QuestionType.Score:
                return (
                    <QuestionScore
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={currentQuestion.answers}
                    />
                );
            case QuestionType.ScoreStarHalf:
                return (
                    <QuestionScoreStar
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        defaultStar={currentQuestion?.defaultStar}
                        options={currentQuestion?.answers}
                        precision={0.5}
                    />
                );
            case QuestionType.ScoreStarFull:
                return (
                    <QuestionScoreStar
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        defaultStar={currentQuestion?.defaultStar}
                        options={currentQuestion?.answers}
                        precision={1}
                    />
                );
            case QuestionType.SliderScale:
                return (
                    <QuestionSliderScale
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        configs={{
                            step: currentQuestion?.step ?? 10,
                            scale: currentQuestion?.scale ?? 100,
                        }}
                        options={currentQuestion?.answers}
                    />
                );
            case QuestionType.Rank:
                return (
                    <QuestionRank
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={currentQuestion?.answers}
                    />
                );
            case QuestionType.MatrixCheckbox:
                return (
                    <>
                        <QuestionMatrixCheckbox
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={currentQuestion?.answers?.matrix}
                            rowsTitle={currentQuestion?.answers?.rowsTitle}
                            columnsTitle={
                                currentQuestion?.answers?.columnsTitle
                            }
                            matrixAnswerType={
                                currentQuestion?.logics?.matrixAnswerType
                            }
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
            case QuestionType.MatrixRadio:
                return (
                    <>
                        <QuestionMatrixRadio
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={currentQuestion?.answers?.matrix}
                            rowsTitle={currentQuestion?.answers?.rowsTitle}
                            columnsTitle={
                                currentQuestion?.answers?.columnsTitle
                            }
                            matrixAnswerType={
                                currentQuestion?.logics?.matrixAnswerType
                            }
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
            case QuestionType.MatrixText:
                return (
                    <>
                        <QuestionMatrixTextbox
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={currentQuestion?.answers?.matrix}
                            rowsTitle={currentQuestion?.answers?.rowsTitle}
                            columnsTitle={
                                currentQuestion?.answers?.columnsTitle
                            }
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
            case QuestionType.Dropdown:
                return (
                    <QuestionDropdown
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={
                            Array.isArray(currentQuestion?.answers)
                                ? currentQuestion?.answers?.map((item) => ({
                                      label: item.label,
                                      value: item.id,
                                  }))
                                : []
                        }
                    />
                );
            case QuestionType.Number:
                return (
                    <QuestionNumber
                        key={questionKey}
                        name={`${questionKey}.answers`}
                    />
                );
            case QuestionType.TextboxRecommend:
                return (
                    <QuestionTextboxRecommend
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={
                            Array.isArray(currentQuestion?.answers)
                                ? currentQuestion?.answers?.map((item) => ({
                                      label: item.label,
                                      value: item.id,
                                  }))
                                : []
                        }
                    />
                );
            case QuestionType.TextboxRecommendMultiChoice:
                return (
                    <QuestionTextboxRecommendMultiChoice
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        options={
                            Array.isArray(currentQuestion?.answers)
                                ? currentQuestion?.answers?.map((item) => ({
                                      label: item.label,
                                      value: item.id,
                                  }))
                                : []
                        }
                    />
                );
            case QuestionType.Date:
                return (
                    <>
                        <QuestionDate
                            key={questionKey}
                            name={`${questionKey}.answers`}
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
            case QuestionType.Image:
                return (
                    <QuestionImage
                        key={questionKey}
                        name={`${questionKey}.answers`}
                        maxFileSize={fileMaxSize}
                    />
                );
            case QuestionType.ImageWithTitle:
                return (
                    <>
                        <QuestionImageWithTitle
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={currentQuestion?.answers?.images}
                            maxFileSize={fileMaxSize}
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
            case QuestionType.Emoji:
                return (
                    <>
                        <QuestionEmoji
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: ['space-between', 'flex-start'],
                                gap: [0, 5],
                            }}
                            options={currentQuestion?.answers}
                            numberOfEmojis={
                                currentQuestion?.numberOfEmojis ?? 0
                            }
                        />
                        <Box
                            sx={{
                                marginTop: '1rem',
                            }}
                        >
                            {errors[questionKey] && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: colors.textError,
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {
                                        // eslint-disable-next-line
                                        (errors[questionKey] as any)?.answers
                                            ?.message
                                    }
                                </Typography>
                            )}
                        </Box>
                    </>
                );
        }
    }, [currentQuestion, errors]);
    // eslint-disable-next-line
    const handleEnterKey = async (event: KeyboardEvent) => {
        if (
            event.key === 'Enter' &&
            currentQuestion &&
            currentQuestion.type !== QuestionType.Textbox
        ) {
            await handleNextQuestion();
        }
    };

    const debouncedEnterKey = useMemo(() => {
        return lodash.debounce(handleEnterKey, 300);
    }, [handleEnterKey]);

    useEffect(() => {
        window.addEventListener('keydown', debouncedEnterKey);
        return () => {
            window.removeEventListener('keydown', debouncedEnterKey);
        };
    }, [currentQuestion, watch, debouncedEnterKey]);

    // watch form changes and handle exclusive checkbox (type 2)
    useEffect(() => {
        if (currentQuestion?.type !== QuestionType.Checkbox) {
            return;
        }

        const questionKey = `question_${currentQuestion.id}`;
        let isUpdating = false;

        const subscription = watch((data) => {
            if (isUpdating) return;

            const selectedAnswers = data[questionKey]?.answers;
            if (!selectedAnswers || selectedAnswers.length === 0) {
                return;
            }

            // Check if any selected answer has type 2 (exclusive option)
            const hasExclusiveAnswer = selectedAnswers.some(
                (answerId: number) => {
                    const answer = currentQuestion.answers.find(
                        (ans: any) => ans.id === answerId // eslint-disable-line @typescript-eslint/no-explicit-any
                    );
                    return answer?.type === 2;
                }
            );

            // If exclusive option is selected, keep only that option
            if (hasExclusiveAnswer) {
                const exclusiveAnswerId = selectedAnswers.find(
                    (answerId: number) => {
                        const answer = currentQuestion.answers.find(
                            (ans: any) => ans.id === answerId // eslint-disable-line @typescript-eslint/no-explicit-any
                        );
                        return answer?.type === 2;
                    }
                );

                isUpdating = true;
                methods.setValue(
                    questionKey + '.answers',
                    [exclusiveAnswerId],
                    {
                        shouldValidate: false,
                    }
                );
                isUpdating = false;
            }
        });

        return () => subscription.unsubscribe();
    }, [
        currentQuestion?.id,
        currentQuestion?.type,
        currentQuestion?.answers,
        watch,
        methods,
    ]);

    return (
        <Box component="div" tabIndex={0}>
            <Grid container sx={{ position: 'relative' }}>
                <Grid
                    item
                    xs={12}
                    md={8}
                    sx={{
                        padding: ['2rem', '2rem', '2rem', '4rem', '4rem'],
                        backgroundImage: [
                            `url(${surveyImage ?? backgroundWithQuestionType[currentQuestion?.type as keyof typeof backgroundWithQuestionType]})`,
                            'unset',
                        ],
                        backgroundColor: ['unset', '#EFF1FF'],
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        height: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid
                            container
                            sx={{
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1rem',
                                    '3rem',
                                    '3rem',
                                ],
                                display: ['none', 'flex'],
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Grid item md={4}>
                                <BrandLogo width={'127px'} />
                            </Grid>
                            <Grid
                                item
                                md={8}
                                sm={8}
                                xs={12}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    justifyContent: 'flex-end',
                                    marginTop: 6,
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        setHistory([]);
                                        setSurveyStep && setSurveyStep(0);
                                    }}
                                    variant="contained"
                                    sx={styles.btn}
                                >
                                    Bắt đầu lại
                                </Button>
                                <ViewMode />
                                <Button
                                    onClick={() =>
                                        setOpenModalShareSurvey(true)
                                    }
                                    variant="contained"
                                    sx={styles.btn}
                                    startIcon={
                                        <ShareOutlinedIcon
                                            sx={{
                                                width: [22, 22, 22, 28, 28],
                                                height: [22, 22, 22, 28, 28],
                                            }}
                                        />
                                    }
                                >
                                    Chia sẻ
                                </Button>
                                {me.creator && (
                                    <IconButton
                                        onClick={() =>
                                            closeSurvey(
                                                Number(surveyState.id),
                                                me
                                            )
                                        }
                                        sx={{
                                            backgroundColor: colors.main,
                                            '&:hover': {
                                                backgroundColor: colors.main,
                                            },
                                            width: [
                                                '2.5rem',
                                                '2rem',
                                                '2rem',
                                                '3rem',
                                                '3rem',
                                            ],
                                            height: [
                                                '2.5rem',
                                                '2rem',
                                                '2rem',
                                                '3rem',
                                                '3rem',
                                            ],
                                        }}
                                    >
                                        <CloseIcon
                                            sx={{ color: 'common.white' }}
                                        />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container marginBottom={2}>
                            <Grid item md={12}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: [welcomeTextColor, '#8B8B8B'],
                                        fontSize: ['2rem'],
                                        fontWeight: 400,
                                        marginTop: ['2.5rem'],
                                        marginBottom: ['0.5'],
                                    }}
                                >
                                    {currentQuestionIndex + 1}/{totalQuestions}
                                </Typography>
                            </Grid>
                        </Grid>
                        <BaseTextField
                            name={`question_${currentQuestion?.id}.questionId`}
                            key={`question_${currentQuestion?.id}.questionId`}
                            sx={{
                                display: 'none',
                            }}
                        />
                        <Grid container>
                            <Grid item md={12} xs={12}>
                                <Typography
                                    variant="h4"
                                    fontWeight={'700'}
                                    fontSize={'35px'}
                                    sx={{
                                        color: [
                                            welcomeTextColor,
                                            'primary.main',
                                        ],
                                        fontWeight: 700,
                                        fontSize: [
                                            '1.5rem',
                                            '1.7rem',
                                            '1.6rem',
                                            '2.5rem',
                                            '2.5rem',
                                        ],
                                        marginBottom: [
                                            '2rem',
                                            '2rem',
                                            '2rem',
                                            '2rem',
                                            '2rem',
                                        ],
                                    }}
                                >
                                    {currentQuestion?.question}
                                </Typography>
                                <Box
                                    sx={{
                                        paddingBottom: ['4rem', 'unset'],
                                    }}
                                >
                                    {renderQuestion()}
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            marginTop={5}
                            sx={{
                                display: ['none', 'flex'],
                            }}
                        >
                            <Grid
                                item
                                md={12}
                                display={'flex'}
                                justifyContent={'flex-start'}
                                gap={2}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={currentQuestionIndex === 0}
                                    onClick={() => handlePreviousQuestion()}
                                    sx={{
                                        borderRadius: '10px',
                                        width: '42px',
                                        height: '42px',
                                        minWidth: '0',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => handleNextQuestion()}
                                    disabled={isSubmitting}
                                    sx={{
                                        borderRadius: '10px',
                                        width: '42px',
                                        height: '42px',
                                        minWidth: '0',
                                        padding: '0',
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </Button>
                                {currentQuestion &&
                                    currentQuestion.type !==
                                        QuestionType.Textbox && (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#00000080',
                                                fontSize: '20px',
                                                display: [
                                                    'none',
                                                    'none',
                                                    'none',
                                                    'flex',
                                                    'flex',
                                                ],
                                                alignItems: 'baseline',
                                                gap: 1,
                                            }}
                                        >
                                            Ấn
                                            <span
                                                style={{
                                                    fontWeight: '700',
                                                }}
                                            >
                                                Enter
                                            </span>
                                            <StyledEnterIcon />
                                        </Typography>
                                    )}
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        backgroundImage: `url(${surveyImage ?? backgroundWithQuestionType[currentQuestion?.type as keyof typeof backgroundWithQuestionType]})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'bottom',
                        backgroundSize: 'cover',
                        padding: [
                            '2rem 3rem',
                            '2rem 3rem',
                            '2rem 3rem',
                            '3rem 4rem',
                            '3rem 4rem',
                        ],
                        display: ['none', 'none', 'flex'],
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        height: '100vh',
                        overflowY: 'auto',
                        aspectRatio: 619 / 1080,
                    }}
                >
                    <Box
                        sx={{
                            fontWeight: '900',
                            fontSize: [
                                '3rem',
                                '3rem',
                                '2.5rem',
                                '3.5rem',
                                '3.5rem',
                            ],
                            lineHeight: [
                                '2rem',
                                '3rem',
                                '3rem',
                                '4rem',
                                '4rem',
                            ],
                            color: welcomeTextColor,
                        }}
                    >
                        {surveyState.title}
                    </Box>
                </Grid>

                {openModalShareSurvey && (
                    <ModalShareSurvey
                        open={openModalShareSurvey}
                        onClose={() => setOpenModalShareSurvey(false)}
                    />
                )}
            </Grid>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '4rem',
                    display: ['flex', 'none'],
                    zIndex: 10,
                }}
            >
                <Button
                    onClick={() => handlePreviousQuestion()}
                    variant="contained"
                    sx={{
                        width: '50%',
                        height: '100%',
                        fontWeight: 900,
                        borderRadius: 0,
                        color: (theme) => theme.palette.primary.main,
                        backgroundColor: (theme) => theme.palette.common.white,
                        ':hover': {
                            backgroundColor: (theme) =>
                                theme.palette.common.white,
                        },
                    }}
                >
                    <ArrowBackIosNewIcon />
                </Button>
                <Button
                    onClick={() => handleNextQuestion()}
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                        width: '50%',
                        height: '100%',
                        borderRadius: 0,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.common.white,
                        ':hover': {
                            backgroundColor: (theme) =>
                                theme.palette.primary.main,
                        },
                    }}
                >
                    <ArrowForwardIosIcon />
                </Button>
            </Box>
        </Box>
    );
};

const styles = createStyleSheet({
    btn: {
        minWidth: ['5rem', '7rem', '7rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});

export default AnswerTheSurvey;
